import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";
import MuiClickAwayListener from "@material-ui/core/ClickAwayListener";

import { LOCALES, LOCALE_LABELS, Locale, useTranslations } from "lib/i18n";
import { useLocaleSwitcher } from "lib/i18n";
import LanguageLink from "lib/components/LanguageLink";

/**
 * Header language picker.
 *
 * A dropdown rather than the footer's flat row, because the list is planned to
 * grow to between three and eight languages and a row that long does not fit the
 * header. The trigger shows the current language rather than only an icon: a lone
 * globe makes the reader open the menu to find out what they are already reading.
 *
 * No flags. A flag is a country, and Spanish is spoken in twenty of them -- a
 * Spanish flag would misdescribe the audience this translation is aimed at, who
 * are Spanish speakers living in the United States.
 *
 * The order is the one written in LOCALES, deliberately not sorted. Alphabetising
 * across scripts is meaningless to the reader once the list mixes Latin with
 * 한국어 or العربية, and a fixed position is something people learn.
 */
const LanguageMenu: React.FC = () => {
  const t = useTranslations("common");
  const { locale } = useLocaleSwitcher();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        setOpen(false);
        triggerRef.current?.focus();
      }
    },
    [open],
  );

  return (
    <MuiClickAwayListener
      onClickAway={close}
      mouseEvent={open ? "onClick" : false}
    >
      <Container onKeyDown={handleKeyDown}>
        <Trigger
          ref={triggerRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={t("languageSwitcher.label")}
          onClick={() => setOpen((value) => !value)}
        >
          <GlobeIcon aria-hidden="true" />
          <TriggerLabel lang={locale}>{LOCALE_LABELS[locale]}</TriggerLabel>
          <Chevron aria-hidden="true" $open={open} />
        </Trigger>

        {open && (
          <Menu role="menu" aria-label={t("languageSwitcher.label")}>
            {LOCALES.map((option: Locale) => (
              <li key={option} role="none">
                <LanguageLink locale={option}>
                  {(label, isActive) => (
                    <Option role="menuitem" $isActive={isActive}>
                      <Check aria-hidden="true">{isActive ? "✓" : ""}</Check>
                      {label}
                    </Option>
                  )}
                </LanguageLink>
              </li>
            ))}
          </Menu>
        )}
      </Container>
    </MuiClickAwayListener>
  );
};

/** Inline so the header does not pull another asset request for 16 pixels. */
const GlobeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
    <path
      d="M1.5 8h13M8 1.5c1.7 1.8 2.6 4.1 2.6 6.5S9.7 12.7 8 14.5C6.3 12.7 5.4 10.4 5.4 8S6.3 3.3 8 1.5z"
      stroke="currentColor"
      strokeWidth="1.3"
    />
  </svg>
);

const Container = styled.div`
  position: relative;
  display: inline-flex;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border: 0;
  border-radius: 6px;
  background: none;
  color: inherit;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const TriggerLabel = styled.span`
  white-space: nowrap;
`;

const Chevron = styled.span<{ $open: boolean }>`
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid currentColor;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "none")};
  transition: transform 0.15s ease;
`;

const Menu = styled.ul`
  position: absolute;
  z-index: 10;
  top: calc(100% + 6px);
  right: 0;
  min-width: 160px;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.16);
`;

const Option = styled.span<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  color: #0b0b0b;
  font-size: 14px;
  font-weight: ${({ $isActive }) => ($isActive ? 700 : 500)};
  white-space: nowrap;

  &:hover {
    background-color: #f3f4f8;
  }
`;

const Check = styled.span`
  width: 12px;
  color: #195dee;
`;

export default LanguageMenu;
